// ---------------------------------------------------------------------------
// YouAreYou (uru) — VerifyEmbedPage (/id/:uuid)
//
// NB: hosted at /id/ — NOT /verify/ — because nginx proxies the /verify/*
// namespace to the MojoVerify backend, so a /verify SPA route 404s on a cold
// recipient load.
//
// A branded recipient experience for verify requests. The verify form itself
// is server-rendered by the MojoVerify backend (§5 of docs/api.md — not part of
// this SPA and no public JSON API to reimplement it natively, unlike docsign).
// The verify pages send no X-Frame-Options / CSP frame-ancestors, so we embed
// the real form inside a youareyou-branded shell and delegate camera access for
// liveness / gov-ID requests via the iframe `allow` attribute.
// ---------------------------------------------------------------------------

import { useState } from "react";
import { useParams } from "react-router-dom";
import BrandMark from "@/components/ui/BrandMark";
import BrandName from "@/components/ui/BrandName";
import Icon from "@/components/ui/Icon";

// Host that serves the recipient-facing verify pages.
const VERIFY_BASE = String(
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_VERIFY_BASE_URL ||
    "https://mojoverify.com",
).replace(/\/$/, "");

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function VerifyEmbedPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const valid = !!uuid && UUID_RE.test(uuid);
  const src = valid ? `${VERIFY_BASE}/verify/basic/${uuid}` : null;
  const [loaded, setLoaded] = useState(false);

  if (!src) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center bg-[#fef0f0] px-6 text-center">
        <BrandMark className="mb-5 h-12 w-12" />
        <h1 className="text-lg font-bold text-[#161b27]">Invalid verification link</h1>
        <p className="mt-2 max-w-xs text-sm text-[#544242]">
          This link is missing a valid request ID. Please check the link you were sent.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-[#fef0f0]">
      {/* Branded chrome */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[#f3d9d9] bg-white/80 px-4 py-3 backdrop-blur-sm sm:px-6">
        <div className="flex items-center gap-2.5">
          <BrandMark className="h-7 w-7" />
          <div className="leading-tight">
            <p className="text-sm font-bold text-[#161b27]">Secure verification</p>
            <p className="text-[11px] text-[#877271]">
              powered by <BrandName showBadge={false} />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-1 text-[11px] font-medium text-[#544242] sm:flex">
            <Icon name="lock" className="text-sm text-[#974446]" />
            End-to-end encrypted
          </span>
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-lg border border-[#dac1c0] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#544242] transition-colors hover:bg-[#faf9ff]"
          >
            <Icon name="open_in_new" className="text-sm" />
            <span className="hidden sm:inline">Open in new tab</span>
          </a>
        </div>
      </header>

      {/* Embedded verify form */}
      <div className="relative flex-1">
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#fef0f0]">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#f08b8b]/30 border-t-[#f08b8b]" />
            <p className="text-xs font-medium text-[#877271]">Loading your secure form…</p>
          </div>
        )}
        <iframe
          src={src}
          title="Identity verification"
          onLoad={() => setLoaded(true)}
          allow="camera; microphone; geolocation; clipboard-write"
          className="h-full w-full border-0 bg-white"
        />
      </div>
    </div>
  );
}
